
import React from 'react';
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Plus, User, Building, Briefcase, Mail, Phone, Hash, Calendar, Star, ChevronDown } from "lucide-react";

interface VariableInserterProps {
  onInsert: (variable: string) => void;
  buttonText?: string;
  showChevron?: boolean;
}

const VariableInserter: React.FC<VariableInserterProps> = ({ 
  onInsert, 
  buttonText = "Insert Variable",
  showChevron = false
}) => {
  const variables = [
    { category: "Personal Information", items: [
      { name: "name", label: "Full Name", icon: User },
      { name: "first_name", label: "First Name", icon: User },
      { name: "email", label: "Email Address", icon: Mail },
      { name: "phone", label: "Phone Number", icon: Phone },
      { name: "handle", label: "Social Media Handle", icon: User },
      { name: "location", label: "Location", icon: User },
      { name: "gender", label: "Gender", icon: User },
    ]},
    { category: "Professional Details", items: [
      { name: "company_name", label: "Company Name", icon: Building },
      { name: "designation", label: "Job Title/Designation", icon: Building },
      { name: "role", label: "Role", icon: Building },
      { name: "brand_name", label: "Brand Name", icon: Building },
      { name: "website", label: "Website", icon: Building },
      { name: "industry", label: "Industry", icon: Building },
    ]},
    { category: "Influencer Metrics", items: [
      { name: "followers_count", label: "Followers Count", icon: Hash },
      { name: "engagement_rate", label: "Engagement Rate", icon: Star },
      { name: "platform", label: "Primary Platform", icon: User },
      { name: "categories", label: "Content Categories", icon: User },
      { name: "rate_per_post", label: "Rate Per Post", icon: Hash },
      { name: "verified_status", label: "Verification Status", icon: Star },
      { name: "languages", label: "Languages", icon: User },
    ]},
    { category: "Campaign Information", items: [
      { name: "campaign_name", label: "Campaign Name", icon: Briefcase },
      { name: "campaign_status", label: "Campaign Status", icon: Briefcase },
      { name: "campaign_budget", label: "Campaign Budget", icon: Hash },
      { name: "campaign_start_date", label: "Campaign Start Date", icon: Calendar },
      { name: "campaign_end_date", label: "Campaign End Date", icon: Calendar },
      { name: "deliverables", label: "Deliverables", icon: Briefcase },
      { name: "deadline", label: "Deadline", icon: Calendar },
    ]},
    { category: "Payment & Contract", items: [
      { name: "fee_committed", label: "Committed Fee", icon: Hash },
      { name: "fee_actual", label: "Actual Fee", icon: Hash },
      { name: "payment_status", label: "Payment Status", icon: Hash },
      { name: "contract_status", label: "Contract Status", icon: Briefcase },
      { name: "payment_due_date", label: "Payment Due Date", icon: Calendar },
      { name: "bonus", label: "Bonus Amount", icon: Hash },
    ]},
    { category: "Contact Details", items: [
      { name: "contact_name", label: "Contact Person Name", icon: User },
      { name: "contact_email", label: "Contact Email", icon: Mail },
      { name: "contact_phone", label: "Contact Phone", icon: Phone },
      { name: "contact_role", label: "Contact Role", icon: User },
    ]},
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="flex items-center gap-1.5 px-3 py-1.5 border-0 rounded-lg text-[#A655F8] bg-[#F6EFFE] hover:bg-[#F6EFFE] hover:text-[#A655F8] font-semibold text-xs transition-colors shadow-sm cursor-pointer"
        >
          <Plus className="w-3.5 h-3.5 stroke-[2.5]" />
          <span>{buttonText}</span>
          {showChevron && <ChevronDown className="w-3 h-3 text-[#A655F8]" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72 max-h-96 overflow-y-auto" align="end">
        {variables.map((category, categoryIndex) => (
          <div key={category.category}>
            <DropdownMenuLabel className="text-xs font-semibold text-gray-500 uppercase">
              {category.category}
            </DropdownMenuLabel>
            {category.items.map((variable) => (
              <DropdownMenuItem
                key={variable.name}
                onClick={() => onInsert(variable.name)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <variable.icon className="w-4 h-4 text-gray-500" />
                <div className="flex flex-col">
                  <span className="text-sm">{variable.label}</span>
                  <span className="text-xs text-gray-500">
                    {`{{${variable.name}}}`}
                  </span>
                </div>
              </DropdownMenuItem>
            ))}
            {categoryIndex < variables.length - 1 && <DropdownMenuSeparator />}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default VariableInserter;
